import { cn } from "@/lib/utils";

/** Shared look for every sidebar row: a raised card marks the active row; a soft fill marks hover and an open menu. */
export const sidebarRowClass = ({
	isActive = false,
	isCollapsed = false,
}: {
	isActive?: boolean;
	isCollapsed?: boolean;
} = {}) =>
	cn(
		"group/row flex shrink-0 cursor-pointer items-center rounded-md text-[13px] font-[450] leading-4 outline-none transition-colors duration-150 ease-out focus-visible:bg-black/[0.05] dark:focus-visible:bg-white/[0.06]",
		isCollapsed ? "size-8 justify-center px-0" : "h-7 w-full px-2.5",
		isActive
			? "rounded-[7px] bg-white font-medium text-foreground shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] dark:bg-[#1C1C1C] dark:text-[#F4F4F4] dark:shadow-[inset_0_0_0_1px_#262626,inset_0_1px_0_rgba(255,255,255,0.05),0_1px_2px_rgba(0,0,0,0.4)]"
			: "text-[#555555] hover:bg-black/[0.03] hover:text-foreground data-[popup-open]:bg-black/[0.03] data-[popup-open]:text-foreground dark:text-[#B4B4B4] dark:hover:bg-white/[0.04] dark:hover:text-[#F5F5F5] dark:data-[popup-open]:bg-white/[0.04] dark:data-[popup-open]:text-[#F5F5F5]",
	);

export const sidebarIconClass = ({
	isActive = false,
}: {
	isActive?: boolean;
} = {}) =>
	cn(
		"flex size-4 shrink-0 items-center justify-center transition-colors duration-150 ease-out [&_svg]:size-4",
		isActive
			? "text-foreground dark:text-[#EDEDED]"
			: "text-[#8A8A8A] group-hover/row:text-foreground dark:text-[#7A7A7A] dark:group-hover/row:text-[#EDEDED]",
	);

export const SIDEBAR_ICON_STROKE = 1.5;

/** Section header label: sentence case, muted, with a caret that turns when the section closes. */
export const SIDEBAR_SECTION_HEADER_CLASS =
	"flex h-6 cursor-pointer items-center gap-1 self-start rounded-md px-2.5 text-xs font-medium leading-4 text-[#8A8A8A] outline-none transition-colors duration-150 ease-out hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring dark:text-[#7A7A7A] dark:hover:text-[#B4B4B4]";

export const SIDEBAR_HEADER_ICON_BUTTON_CLASS =
	"flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-[#8A8A8A] outline-none transition-colors duration-150 ease-out hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring dark:text-[#7A7A7A] dark:hover:bg-white/[0.06] dark:hover:text-[#EDEDED]";

/** The expanded width is set once here; desktop and the mobile sheet both read the var. */
const SIDEBAR_EXPANDED_WIDTH_VAR = "[--sidebar-expanded-width:216px]";

export const sidebarWidthClass = ({
	expanded,
	isMobileSheet,
}: {
	expanded: boolean;
	isMobileSheet: boolean;
}) => {
	if (isMobileSheet)
		return cn(SIDEBAR_EXPANDED_WIDTH_VAR, "min-w-(--sidebar-expanded-width)");
	if (expanded)
		return cn(
			SIDEBAR_EXPANDED_WIDTH_VAR,
			"min-w-(--sidebar-expanded-width) max-w-(--sidebar-expanded-width)",
		);
	return "min-w-[52px] max-w-[52px]";
};

/** Label sits after the icon when expanded and takes no space when the rail is collapsed. */
export const sidebarRowContentClass = ({
	isCollapsed = false,
}: {
	isCollapsed?: boolean;
} = {}) =>
	cn(
		"flex min-w-0 items-center",
		isCollapsed ? "justify-center" : "flex-1 gap-2.5",
	);
